import { useEffect, useState } from "react";

import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import { useNavigate, useParams } from "react-router";

import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { postNotification } from "../../components/utilities/Notifications";

import { cardUtility } from "../cards/CardUtil";
import CardCard, {
  CardImagePreview,
  CardStack,
} from "../cards/components/CardCard";
import ArmyStats from "./components/ArmyStats";
import FirstHand from "./components/FirstHand";
import { ArmyListCopyModal } from "./components/ArmyListModals";
import { useContext } from "react";
import {
  getData,
  postData,
  titleCase,
} from "../../components/utilities/UtilityFunctions";
import ArmyListSocialsView from "./components/ArmyListSocialsView";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { getEnvironmentVariable } from "../../global/environmentVariables";
const IS_DEMO = getEnvironmentVariable("IS_DEMO") === "true";
const COMPANY_FLAG = getEnvironmentVariable("COMPANY_FLAG");

function a11yProps(index) {
  return {
    id: `card-view-tab-${index}`,
    "aria-controls": `card-view-${index}`,
  };
}

export default function ArmyListBuilderView(props) {
  const { heading } = props;
  let { armyListId } = useParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const cardUtil = cardUtility();

  const [loadingPage, setLoadingPage] = useState(true);
  const [armyList, setArmyList] = useState(null);
  const [cardView, setCardView] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [deckFormat, setDeckFormat] = useState(null);

  const [copyModal, setCopyModal] = useState(false);

  const handleCardViewChange = (e, newValue) => {
    setCardView(newValue);
  };

  useEffect(() => {
    const loadPage = async () => {
      setLoadingPage(true);

      var apiArmyList;
      if (armyListId) {
        try {
          apiArmyList = await getData(`/armylists/${armyListId}`, {
            queryStringParameters: {
              "expand[]": [
                "player",
                "event",
                "gameSystem",
                "user",
                "army",
                "character",
              ],
            },
          });

          if (!apiArmyList.armyListContent && apiArmyList.armyListText) {
            // legacy list
            navigate(`/legacy/list/${armyListId}`);
            return;
          }

          setAnalytics(apiArmyList?.analytics);

          let apiDeckFormat;
          if (apiArmyList.deckFormat) {
            for (let df of cardUtil.deckFormats) {
              if (df.name === apiArmyList.deckFormat) {
                apiDeckFormat = df;
                setDeckFormat(df);
                break;
              }
            }
          }

          if (apiArmyList.armyListContent) {
            for (let cardCategoryId of Object.keys(
              apiArmyList.armyListContent
            )) {
              let expand = apiDeckFormat?.categories?.[cardCategoryId] || {};
              let qty = 0;
              if (apiArmyList.armyListContent[cardCategoryId].cards) {
                for (let card of Object.values(
                  apiArmyList.armyListContent[cardCategoryId].cards
                )) {
                  qty += card.qty || 0;
                }
              }

              apiArmyList.armyListContent[cardCategoryId] = {
                ...apiArmyList.armyListContent[cardCategoryId],
                ...expand,
                qty: qty,
              };
            }
          }
        } catch (err) {
          postNotification("error", err);
        }
      }

      if (!apiArmyList) {
        postNotification("error", `No ${IS_DEMO ? "decks" : "lists"} found.`);
        navigate("/");
        return;
      }

      setArmyList(apiArmyList);

      setLoadingPage(false);

      try {
        var apiAnalytics = await postData(`/armylists/${armyListId}/analytics`);
        setAnalytics(apiAnalytics);
      } catch (err) {
        setAnalytics(null);
      }
    };
    loadPage();
    // eslint-disable-next-line
  }, []);

  return (
    <Container
      sx={{
        pt: 2,
        pb: 2,
      }}
      maxWidth="lg"
    >
      {!loadingPage && (
        <>
          <Grid container sx={{ gap: 2 }}>
            <Grid
              item
              xs={12}
              sx={{
                p: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: 1,
                boxShadow: 1,
                backgroundColor: "background.secondary",
              }}
            >
              <Typography variant="h3">
                {heading || armyList?.gameSystem?.listLabel || "Army List"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Grid container sx={{ gap: 2 }}>
                <Grid
                  item
                  xs={12}
                  lg={4}
                  sx={{
                    p: 2,
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 1,
                    backgroundColor: "background.secondary",
                  }}
                >
                  <Grid container>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        pb: 1,
                        display: "flex",
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                      }}
                    >
                      <Grid item sx={{ flex: 1 }}>
                        <Typography variant="h4">
                          {armyList?.name || armyList?.gameSystem?.listLabel}
                        </Typography>
                      </Grid>
                      <Grid item sx={{ textAlign: "right" }}>
                        <ButtonGroup variant="outlined" color="primary">
                          <Button
                            onClick={() => {
                              setCopyModal(true);
                            }}
                          >
                            Copy
                          </Button>
                          {authContext?.authState?.identity?.userId ===
                            armyList?.user?.id && (
                            <Button href={`/deck/${armyList?.id}/deck-builder`}>
                              Edit
                            </Button>
                          )}
                        </ButtonGroup>
                        <ArmyListCopyModal
                          open={copyModal}
                          setOpen={setCopyModal}
                          armyList={armyList}
                        />
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      {armyList?.deckFormat && (
                        <Typography variant="body1" gutterBottom>
                          Format: {armyList.deckFormat}
                        </Typography>
                      )}
                      {armyList?.army?.name && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            mb: 1,
                          }}
                        >
                          <Typography>{`${armyList?.gameSystem?.factionLabel || "Faction"}: ${armyList?.army.name}`}</Typography>
                        </Box>
                      )}
                      {armyList?.subFaction?.name && (
                        <Typography variant="body1" gutterBottom>
                          <Typography>{`${armyList?.gameSystem?.subFactionLabel || "Sub Faction"}: ${armyList?.subFaction.name}`}</Typography>
                        </Typography>
                      )}
                      {armyList?.character?.name && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            mb: 1,
                          }}
                        >
                          <CardImagePreview
                            card={armyList?.character}
                            superDense
                          />
                          <Typography sx={{ ml: 1 }}>
                            {`${armyList?.gameSystem?.characterLabel || armyList?.gameSystem?.subFactionLabel || "Character"}: ${armyList?.character.name}`}
                          </Typography>
                        </Box>
                      )}
                      {(armyList?.user?.firstName ||
                        armyList?.user?.lastName) && (
                        <Typography variant="body1" gutterBottom>
                          by{" "}
                          {`${armyList?.user?.firstName || ""} ${
                            armyList?.user?.lastName || ""
                          }`.trim()}
                        </Typography>
                      )}
                      {analytics && (
                        <>
                          <Typography variant="body1" gutterBottom>
                            Views: {analytics?.views || 0}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            Copies: {analytics?.copies || 0}
                          </Typography>
                        </>
                      )}
                    </Grid>
                    {armyList?.tags?.length > 0 && (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          "& .MuiChip-root:not(:first-of-type)": {
                            ml: 1,
                          },
                        }}
                      >
                        {armyList.tags.map((tag, index) => {
                          return (
                            <Chip
                              key={index}
                              label={`#${tag}`}
                              color="primary"
                              variant="outlined"
                            />
                          );
                        })}
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                <Grid
                  item
                  xs={12}
                  lg={8}
                  sx={{
                    maxWidth: [
                      "100%",
                      "100%",
                      "100%",
                      `calc(${(100 / 3) * 2}% - 16px)`,
                    ], // deal with spacing bug
                  }}
                >
                  <Grid container sx={{ gap: 2 }}>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        borderRadius: 1,
                        boxShadow: 1,
                        backgroundColor: "background.secondary",
                      }}
                    >
                      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                        <Tabs
                          value={cardView}
                          onChange={handleCardViewChange}
                          sx={{
                            "& .MuiTabs-flexContainer": {
                              justifyContent: "end",
                            },
                            color: "text.primary",
                          }}
                          textColor="inherit"
                        >
                          <Tab label="List" {...a11yProps(0)} />
                          <Tab label="Card View" {...a11yProps(1)} />
                          {/* <Tab label="Socials View" {...a11yProps(2)} /> */}
                        </Tabs>
                      </Box>
                      {cardView === 0 && (
                        <Grid container>
                          {COMPANY_FLAG === "shard-bugs" &&
                            armyList?.character && (
                              <Grid
                                item
                                xs={12}
                                sx={{
                                  p: 1,
                                }}
                              >
                                <Grid container>
                                  <Grid
                                    item
                                    xs={12}
                                    sx={{
                                      p: 1,
                                      backgroundColor: "background.secondary",
                                      color: null,
                                    }}
                                  >
                                    <Typography variant="h5">
                                      {`${armyList?.gameSystem?.characterLabel || armyList?.gameSystem?.subFactionLabel || "Character"}`}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12}>
                                    <CardCard
                                      card={armyList?.character}
                                      superDense
                                      hover
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                            )}
                          {armyList?.armyListContent &&
                            Object.values(armyList?.armyListContent).length >
                              0 &&
                            Object.values(armyList?.armyListContent)
                              .sort((a, b) => {
                                return a.order - b.order;
                              })
                              .map((cardCategory, index) => {
                                var sizeError =
                                  (cardCategory.size?.min !== undefined &&
                                    (cardCategory.qty || 0) <
                                      cardCategory.size?.min) ||
                                  (cardCategory.size?.max !== undefined &&
                                    (cardCategory.qty || 0) >
                                      cardCategory.size?.max);
                                let cards = Object.values(cardCategory.cards);
                                cards.sort((a, b) => {
                                  if (
                                    cardCategory.name === "Character" &&
                                    armyList?.characterId
                                  ) {
                                    if (a.id === armyList.characterId) {
                                      return -1;
                                    }
                                    if (b.id === armyList.characterId) {
                                      return 1;
                                    }
                                  }

                                  if (b.qty !== a.qty) {
                                    return b.qty - a.qty;
                                  }
                                  var nameA = a.name?.toLowerCase();
                                  var nameB = b.name?.toLowerCase();
                                  if (nameA > nameB) return 1;
                                  if (nameA < nameB) return -1;
                                  return 0;
                                });
                                return (
                                  <Grid
                                    key={index}
                                    item
                                    xs={12}
                                    sx={{
                                      p: 1,
                                    }}
                                  >
                                    <Grid container>
                                      <Grid
                                        item
                                        xs={12}
                                        sx={{
                                          p: 1,
                                          backgroundColor: sizeError
                                            ? "error.main"
                                            : "background.secondary",
                                          color: sizeError
                                            ? "error.contrastText"
                                            : null,
                                          borderRadius: sizeError ? 1 : null,
                                        }}
                                      >
                                        <Typography variant="h5">
                                          {`${cardCategory.name} (${
                                            cardCategory.qty || 0
                                          }${
                                            cardCategory.size?.max !== undefined
                                              ? `/${cardCategory.size.max}`
                                              : ""
                                          } card${
                                            cardCategory.qty !== 1 ? "s" : ""
                                          })`}
                                        </Typography>
                                      </Grid>
                                      {cards?.length > 0 &&
                                        cards.map((card) => {
                                          let cardError =
                                            card.maxQty !== undefined
                                              ? card.qty > card.maxQty
                                              : card.qty >
                                                (card.copies ||
                                                  deckFormat?.cards?.size
                                                    ?.max ||
                                                  4);
                                          return (
                                            <Grid item xs={12}>
                                              <CardCard
                                                key={card.id}
                                                card={card}
                                                superDense
                                                hover
                                                error={cardError}
                                              />
                                            </Grid>
                                          );
                                        })}
                                    </Grid>
                                  </Grid>
                                );
                              })}
                        </Grid>
                      )}

                      {cardView === 1 && armyList?.armyListContent && (
                        <Grid
                          container
                          spacing={1}
                          sx={{
                            justifyContent: "center",
                            mt: 1,
                            mb: 1,
                          }}
                        >
                          {Object.values(armyList?.armyListContent).length >
                            0 &&
                            Object.values(
                              COMPANY_FLAG === "shard-bugs" &&
                                armyList?.character
                                ? {
                                    "Cosmic Avatar": {
                                      name: "Cosmic Avatar",
                                      cards: {
                                        [armyList?.character?.id]: {
                                          ...armyList?.character,
                                          qty: 1,
                                          imageUrl: `${armyList?.character?.imageFile?.cloudFrontDomain}${armyList?.character?.imageFile?.filename}`,
                                        },
                                      },
                                    },
                                    ...armyList?.armyListContent,
                                  }
                                : armyList?.armyListContent
                            )
                              .sort((a, b) => {
                                return a.order - b.order;
                              })
                              .map((cardCategory, index) => {
                                let cards = Object.values(cardCategory.cards);
                                cards.sort((a, b) => {
                                  if (
                                    cardCategory.name === "Character" &&
                                    armyList?.characterId
                                  ) {
                                    if (a.id === armyList.characterId) {
                                      return -1;
                                    }
                                    if (b.id === armyList.characterId) {
                                      return 1;
                                    }
                                  }

                                  if (b.qty !== a.qty) {
                                    return b.qty - a.qty;
                                  }
                                  var nameA = a.name.toLowerCase();
                                  var nameB = b.name.toLowerCase();
                                  if (nameA > nameB) return 1;
                                  if (nameA < nameB) return -1;
                                  return 0;
                                });
                                return (
                                  <>
                                    {cards?.length > 0 &&
                                      Object.values(cards).map((card) => {
                                        let cardError =
                                          card.maxQty !== undefined
                                            ? card.qty > card.maxQty
                                            : card.qty >
                                              (deckFormat?.cards?.size?.max ||
                                                4);
                                        return (
                                          <Grid item>
                                            <CardStack
                                              hover
                                              key={card.id}
                                              card={card}
                                              error={cardError}
                                            />
                                          </Grid>
                                        );
                                      })}
                                  </>
                                );
                              })}
                        </Grid>
                      )}

                      {cardView === 2 && armyList?.armyListContent && (
                        <Grid
                          container
                          spacing={1}
                          sx={{
                            justifyContent: "center",
                            mt: 1,
                            mb: 1,
                          }}
                        >
                          <Grid item xs={12} sx={{ p: 1, ml: 1 }}>
                            <ArmyListSocialsView deck={armyList} />
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>

                {armyList?.listStatus !== "passed" && (
                  <Grid
                    item
                    xs={12}
                    sx={{
                      p: 1,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      boxShadow: 1,
                      backgroundColor: "background.secondary",
                    }}
                  >
                    <Grid container>
                      <Grid
                        item
                        xs={12}
                        sx={{
                          p: 1,
                        }}
                      >
                        <Typography variant="h5">
                          List Status {titleCase(armyList?.listStatus || "")}
                          {armyList?.listStatus === "pending" ? (
                            <CircularProgress size="16" />
                          ) : null}
                        </Typography>
                      </Grid>
                      {armyList?.listErrors?.length > 0 &&
                        armyList.listErrors.map((listError, index) => (
                          <Grid
                            key={index}
                            item
                            xs={12}
                            sx={{
                              p: 1,
                            }}
                          >
                            {listError}
                          </Grid>
                        ))}
                    </Grid>
                  </Grid>
                )}

                <Grid
                  item
                  xs={12}
                  sx={{
                    p: 1,
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 1,
                    backgroundColor: "background.secondary",
                  }}
                >
                  <FirstHand armyList={armyList} />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{
                    p: 1,
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 1,
                    backgroundColor: "background.secondary",
                  }}
                >
                  <ArmyStats
                    cardUtil={cardUtil}
                    armyList={armyList}
                    deckFormat={deckFormat}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}

      {loadingPage && <ModalLoadingFull />}
    </Container>
  );
}
